import type { ReactNode } from "react";
import { DuviNicheSiteNavBar } from "@/components/duvi-niche-site-nav-bar";
import type { DuviNicheSiteNavBarLink } from "@/components/duvi-niche-site-nav-bar";
import { DuviNicheSiteFooter } from "@/components/duvi-niche-site-footer";
import type { DuviNicheSiteFooterColumn } from "@/components/duvi-niche-site-footer";

export interface DuviNicheSitePageLayoutProps {
  /** Sign-up hand-off to business registration on `app.<niche-domain>`. */
  registerUrl: string;
  children: ReactNode;
}

const NAV_LINKS: DuviNicheSiteNavBarLink[] = [
  { label: "Home", to: "/", end: true },
  { label: "Pricing", to: "/pricing" },
];

const FOOTER_BLURB =
  "Fishwalkers gives every walker their own booking page, so local fish owners can find you and book with confidence.";

/**
 * DuviNicheSitePageLayout — the frame both routes share: the sticky nav, the
 * page, and the dark footer band, matching the chrome the bespoke Fishwalkers
 * brochure prototype puts on each of its artboards
 * (duvi-design/exports/niches/fishwalkers/brochure.zip).
 *
 * The chrome's copy is static site content and lives here rather than in a
 * page hook — it does not vary by route, and the only value that does vary by
 * environment (the register hand-off) arrives as a prop.
 *
 * Only the links this site can honour are rendered. The prototype's "Set up
 * your round" and "Your walk types" footer entries are placeholder labels for
 * screens the business portal owns and this brochure cannot link to yet, so
 * they are not reproduced as dead anchors; Terms and Privacy stay as the
 * placeholders the previous build shipped, pending real legal pages.
 */
export function DuviNicheSitePageLayout({
  registerUrl,
  children,
}: DuviNicheSitePageLayoutProps) {
  const footerColumns: DuviNicheSiteFooterColumn[] = [
    {
      title: "Fishwalkers",
      links: [
        { label: "Home", to: "/" },
        { label: "Pricing", to: "/pricing" },
      ],
    },
    {
      title: "For walkers",
      links: [{ label: "Sign up", href: registerUrl }],
    },
    {
      title: "Legal",
      links: [
        { label: "Terms", href: "#" },
        { label: "Privacy", href: "#" },
      ],
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[var(--canvas)]">
      <DuviNicheSiteNavBar links={NAV_LINKS} registerUrl={registerUrl} />
      <main className="flex-1">{children}</main>
      <DuviNicheSiteFooter
        blurb={FOOTER_BLURB}
        columns={footerColumns}
        copyright={`© ${new Date().getFullYear()} Fishwalkers. All rights reserved.`}
      />
    </div>
  );
}
